// R-162 §10.1 / WU-3.7 — shown when a supervisor parks the operator's feed
// lane while they keyed the in-flight doc. The accept response carries
// feedBlocked; the VS pops this modal and stops the feed. The only forward
// action is "Pick another feed" (→ /queues). Mirrors CrossBatchConfirmModal's
// backdrop/card pattern (shared .cross-batch-modal__* styles in
// VerificationStation.css).
//
// WU-2.4 /simplify — the eviction refeed path (resolveRefeedOutcome) also
// diverts here (VerificationStation "blocked" outcome), but an evicted
// operator is a different situation than a supervisor-parked lane: the copy
// must say "you were moved off this queue", not "this queue is on hold" (the
// two stacked read as contradictory). `cause` selects title/intro; the
// reason line always renders whatever the caller passes (raw for eviction,
// the supervisor's free text for a block) — no reformatting here.

interface Props {
  /** Supervisor's free-form reason (≤200 chars) for parking the lane, or the raw eviction reason. */
  reason: string;
  /** WU-2.4 — "blocked" (supervisor parked the lane) is the default, byte-identical to the original copy. "evicted" is the operator-moved-off-the-lane case. */
  cause?: "blocked" | "evicted";
  /** Navigate the operator back to the queue dashboard to pick a new lane. */
  onPickAnotherFeed: () => void;
}

const COPY = {
  blocked: {
    title: "Queue blocked",
    intro: "This queue was just placed on hold by a supervisor.",
  },
  evicted: {
    title: "Removed from this queue",
    intro: "A supervisor has moved you off this queue.",
  },
} as const;

export function QueueBlockedModal({ reason, cause = "blocked", onPickAnotherFeed }: Props) {
  const { title, intro } = COPY[cause];
  return (
    <div className="cross-batch-modal__backdrop">
      <div
        className="cross-batch-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="queue-blocked-title"
      >
        <h2 id="queue-blocked-title" className="cross-batch-modal__title">
          🔒 {title}
        </h2>
        <p className="cross-batch-modal__body">{intro}</p>
        <p className="cross-batch-modal__body">
          <strong>Reason:</strong> {reason}
        </p>
        <div className="cross-batch-modal__actions">
          <button
            type="button"
            className="cross-batch-modal__btn-primary"
            onClick={onPickAnotherFeed}
          >
            Pick another feed
          </button>
        </div>
      </div>
    </div>
  );
}
